/*
  Every list a trial cancellation can answer with, in one place.

  These exist three times over: here, as Zod enums in the controller, and as
  CHECK constraints in migration 195. Three copies drift, and the failure mode
  is the worst kind: the flow offers an option, the API accepts it, and
  Postgres rejects the insert, so the answer is lost at the last step with a
  500. trialCancellationParity.test.ts holds all three to one contents.
*/
export const CANCEL_REASONS = [
    'no_time',
    'could_not_get_working',
    'too_complex',
    'not_what_expected',
    'too_expensive',
    'switching',
    'other',
];
/* How much more time they would have needed. */
/*
  Only spans a seven day extension can actually meet.

  "A few weeks" and "next quarter" were here and are not any more. Somebody
  who needs a quarter is not somebody this offer saves, and asking them to
  pick it collects a disappointment rather than a lead.
*/
export const NO_TIME_DETAILS = ['few_days', 'another_week', 'not_sure'];
/* Where it stopped. Mechanical only: this branch is about breakage. */
export const COULD_NOT_GET_WORKING_DETAILS = [
    'connecting_site',
    'deploying_schema',
    'confirming_it_worked',
    'app_broke',
];
/*
  What the hard part was. Comprehension only: this branch is about the product
  running fine and the person not being able to follow it. "I could not tell
  what to do next" lives here and only here. It was briefly in both branches,
  which would have collapsed the two into one useless bucket.
*/
export const TOO_COMPLEX_DETAILS = [
    'results_unclear',
    'not_enough_knowledge',
    'too_many_steps',
    'unclear_next_step',
    'want_it_done_for_me',
];
export const TOO_EXPENSIVE_DETAILS = [
    'fewer_pages_for_less',
    'pay_yearly',
    'more_at_this_price',
    'no_price_works',
];
/**
 * The union stored in reason_detail.
 *
 * Which values are legal for which reason is enforced by the request schema,
 * not by SQL: Postgres cannot express the pairing without a trigger, and a
 * trigger is a worse place for it than Zod.
 */
export const CANCEL_REASON_DETAILS = [
    ...NO_TIME_DETAILS,
    ...COULD_NOT_GET_WORKING_DETAILS,
    ...TOO_COMPLEX_DETAILS,
    ...TOO_EXPENSIVE_DETAILS,
];
/** Which branch a detail belongs to. The controller validates the pair against this. */
export const DETAILS_BY_REASON = {
    no_time: NO_TIME_DETAILS,
    could_not_get_working: COULD_NOT_GET_WORKING_DETAILS,
    too_complex: TOO_COMPLEX_DETAILS,
    too_expensive: TOO_EXPENSIVE_DETAILS,
};
/** Reasons whose follow-up is a sentence rather than a list. */
export const FREE_TEXT_REASONS = ['not_what_expected', 'switching'];
export const CANCEL_OFFERS = [
    'extend_trial',
    'downgrade_essential',
    'setup_help',
    'talk_to_us',
    'managed_referral',
    'none',
];
export const CANCEL_OFFER_OUTCOMES = ['accepted', 'declined', 'not_shown'];
/*
  Why somebody is worth an email. managed_interest is the person who asked for
  it. The other two are ours: a Portfolio trial walking away is worth a minute
  whatever the reason, and somebody who got pages live and left anyway is the
  only group whose departure is a verdict on the product rather than on the
  onboarding.
*/
export const CANCEL_OUTREACH_REASONS = [
    'managed_interest',
    'setup_help_requested',
    'high_value',
    'activated_and_left',
];
export const CANCEL_OUTREACH_STATUSES = ['pending', 'contacted', 'closed'];
